import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Shield, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { USER_ROLE } from '@/utils/constants';

const AdminAuthPage = () => {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [loading, setLoading] = useState(false);

  const [loginData, setLoginData] = useState({
    email: '',
    password: '',
  });

  const [registerData, setRegisterData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: USER_ROLE.BROKER,
    phone: '',
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await login(loginData.email, loginData.password);
      if (result.user.role !== USER_ROLE.BROKER && result.user.role !== USER_ROLE.ADMIN) {
        toast.error('Access denied. Admin/Broker access only.');
        return;
      }
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(registerData);
      toast.success('Registration successful! Please login.');
      setRegisterData({
        email: '',
        password: '',
        full_name: '',
        role: USER_ROLE.BROKER,
        phone: '',
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-8">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_broker-claims-1/artifacts/duvrk5eb_kind-insurance-logo.png" 
              alt="Kind Insurance Brokers" 
              className="h-16 w-auto"
            />
          </div>
          <div className="flex items-center justify-center gap-2 text-slate-400">
            <Lock className="h-4 w-4" />
            <span className="text-sm" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Admin & Broker Access Only
            </span>
          </div>
        </div>

        <Card className="border-slate-700 bg-slate-800">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="admin-login-tab">Login</TabsTrigger>
              <TabsTrigger value="register" data-testid="admin-register-tab">Register</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Admin/Broker Login</CardTitle>
                <CardDescription className="text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                  Enter your credentials to access the portal
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleLogin} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-login-email" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>Email</Label>
                    <Input
                      id="admin-login-email"
                      data-testid="admin-login-email-input"
                      type="email"
                      placeholder="admin@example.com"
                      className="bg-slate-700 border-slate-600 text-white"
                      value={loginData.email}
                      onChange={(e) => setLoginData({ ...loginData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-login-password" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>Password</Label>
                    <Input
                      id="admin-login-password"
                      data-testid="admin-login-password-input"
                      type="password"
                      placeholder="••••••••"
                      className="bg-slate-700 border-slate-600 text-white"
                      value={loginData.password}
                      onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="admin-login-submit-button"
                    className="w-full" 
                    disabled={loading}
                    style={{ fontFamily: 'Public Sans, sans-serif' }}
                  >
                    {loading ? 'Logging in...' : 'Login'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>

            <TabsContent value="register">
              <CardHeader>
                <CardTitle className="text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Register Admin/Broker</CardTitle>
                <CardDescription className="text-slate-400" style={{ fontFamily: 'Public Sans, sans-serif' }}>
                  Create a new admin or broker account
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleRegister} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="admin-full-name" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>Full Name</Label>
                    <Input
                      id="admin-full-name"
                      data-testid="admin-register-name-input"
                      placeholder="John Doe"
                      className="bg-slate-700 border-slate-600 text-white"
                      value={registerData.full_name}
                      onChange={(e) => setRegisterData({ ...registerData, full_name: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-email" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>Email</Label>
                    <Input
                      id="admin-email"
                      data-testid="admin-register-email-input"
                      type="email"
                      placeholder="admin@example.com"
                      className="bg-slate-700 border-slate-600 text-white"
                      value={registerData.email}
                      onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-phone" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>Phone (Optional)</Label>
                    <Input
                      id="admin-phone"
                      data-testid="admin-register-phone-input"
                      type="tel"
                      placeholder="+234 800 000 0000"
                      className="bg-slate-700 border-slate-600 text-white"
                      value={registerData.phone}
                      onChange={(e) => setRegisterData({ ...registerData, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-role" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>Role</Label>
                    <select
                      id="admin-role"
                      data-testid="admin-register-role-select"
                      className="flex h-10 w-full rounded-md border border-slate-600 bg-slate-700 text-white px-3 py-2 text-sm"
                      value={registerData.role}
                      onChange={(e) => setRegisterData({ ...registerData, role: e.target.value })}
                      required
                    >
                      <option value={USER_ROLE.BROKER}>Insurance Broker</option>
                      <option value={USER_ROLE.ADMIN}>Admin</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="admin-password" className="text-slate-300" style={{ fontFamily: 'Public Sans, sans-serif' }}>Password</Label>
                    <Input
                      id="admin-password"
                      data-testid="admin-register-password-input"
                      type="password"
                      placeholder="••••••••"
                      className="bg-slate-700 border-slate-600 text-white"
                      value={registerData.password}
                      onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
                      required
                    />
                  </div>
                  <Button 
                    type="submit" 
                    data-testid="admin-register-submit-button"
                    className="w-full" 
                    disabled={loading}
                    style={{ fontFamily: 'Public Sans, sans-serif' }}
                  >
                    {loading ? 'Creating account...' : 'Create Account'}
                  </Button>
                </form>
              </CardContent>
            </TabsContent>
          </Tabs>
        </Card>

        <div className="text-center mt-4">
          <Button
            variant="link"
            className="text-slate-400 hover:text-white"
            onClick={() => navigate('/auth')}
          >
            Client Portal Login →
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AdminAuthPage;
